import {
  Menu,
  MenuButton,
  MenuItem,
  MenuItems,
  Popover,
  PopoverPanel,
  Transition,
} from '@headlessui/react'
import {
  ArchiveBoxIcon,
  ArrowPathIcon,
  ArrowUturnLeftIcon,
  CheckIcon,
  EllipsisHorizontalIcon,
  EnvelopeIcon,
  EnvelopeOpenIcon,
} from '@heroicons/react/24/outline'
import { BellIcon } from '@heroicons/react/24/solid'
import dayjs from 'dayjs'
import relativeTime from 'dayjs/plugin/relativeTime'
import updateLocale from 'dayjs/plugin/updateLocale'
import * as _ from 'lodash-es'
import { observer } from 'mobx-react'
import React, { Fragment, useContext, useEffect, useMemo, useRef, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { twMerge as mergeClassNames } from 'tailwind-merge'

import { FilterIcon } from '@/components/icons'
import { Button } from '@/components/ui/Button'
import {
  getNotifications,
  markAllNotificationsAsArchived,
  markAllNotificationsAsRead,
  markNotificationAsArchived,
  markNotificationAsRead,
  markNotificationAsUnarchived,
  markNotificationAsUnread,
} from '@/services/notifications.service'
import { AnnouncementStoreContext } from '@/stores/AnnouncementStore'
import { NotificationStoreContext } from '@/stores/NotificationStore'
import { UserStoreContext } from '@/stores/UserStore'
import { track } from '@/utils/analytics'
import { NOTIFICATION_MENU_OPENED, NOTIFICATION_CLICKED } from '@/utils/analytics.constants'
import { joinClassNames, toast } from '@/utils/helpers'

import {
  ABSOLUTE_DATE_FORMAT,
  BADGE_MAX_DISPLAY,
  BADGE_MAX_DISPLAY_LABEL,
  FILTER_OPTIONS,
  RELATIVE_TIME_FALLBACK_DAYS,
  RELATIVE_TIME_LOCALE,
} from './constants'

dayjs.extend(relativeTime)
dayjs.extend(updateLocale)
dayjs.updateLocale('en', { relativeTime: RELATIVE_TIME_LOCALE })

const formatNotificationTime = (timestamp) => {
  const then = dayjs(timestamp)
  if (dayjs().diff(then, 'day') >= RELATIVE_TIME_FALLBACK_DAYS)
    return then.format(ABSOLUTE_DATE_FORMAT)
  return then.fromNow()
}

const formatBadgeCount = (count) => (count > BADGE_MAX_DISPLAY ? BADGE_MAX_DISPLAY_LABEL : count)

const Notifications = observer(() => {
  // Context
  const {
    currentPage,
    notifications,
    unreadNotificationsCount,
    nextPage,
    triggerAlert,
    hasNewNotifications,
    isMenuOpen,
    setCurrentPage,
    setNotifications,
    setTriggerAlert,
    setMenuOpen,
    setHasNewNotifications,
  } = useContext(NotificationStoreContext)
  const announcementStore = useContext(AnnouncementStoreContext)
  const { isImpersonating } = useContext(UserStoreContext)
  const navigate = useNavigate()

  // Site-wide announcements shown in the bell, and their contribution to the
  // unread badge. Announcements load on auth (independent of the user's own
  // notification settings), so they're always available here.
  const { bellAnnouncements } = announcementStore
  const totalUnreadCount = (unreadNotificationsCount || 0) + announcementStore.unreadCount

  // State
  const [loading, setLoading] = useState(false)
  const [loadingMore, setLoadingMore] = useState(false)
  const [filter, setFilter] = useState('unread_read')

  const buttonRef = useRef()
  const panelRef = useRef()

  const handleError = (message) => toast(message, 'error')

  /**
   * Manually close the popover panel if the user clicks outside of the button or panel.
   * @param {object} event
   */
  const handleClickOutside = (event) => {
    if (!buttonRef.current?.contains(event.target) && !panelRef.current?.contains(event.target)) {
      event.stopPropagation()
      setMenuOpen(false)
    }
  }

  /**
   * Set up the event listener for clicking outside of the popover panel.
   */
  useEffect(() => {
    document.addEventListener('mousedown', handleClickOutside)

    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [])

  /**
   * Trigger the alert animation when there are new notifications and reset it after 2 seconds.
   */
  useEffect(() => {
    if (triggerAlert) {
      setTimeout(() => {
        setTriggerAlert(false)
      }, 1500)
    }
  }, [triggerAlert])

  /**
   * Get updated notifications based on the filter.
   */
  useEffect(() => {
    if (isMenuOpen) {
      setCurrentPage(1)
      fetchNotifications()
    }
  }, [filter, isMenuOpen])

  const fetchNotifications = () => {
    // Reset the new notification state since we are loading all notifications
    setHasNewNotifications(false)

    getNotifications(
      `/notifications/?${_.find(FILTER_OPTIONS, (f) => f.value === filter)?.filter}`,
      currentPage,
      setLoading,
      handleError,
      setNotifications,
      true,
    )
  }

  const NOTIFICATION_SETTING_OPTIONS = useMemo(() => {
    const options = []

    // WARP-567 — these mark the *viewed* user's notifications read or archived, which an
    // impersonated session cannot do. Offering them would only produce an error toast.
    if (notifications.length > 0 && !isImpersonating) {
      options.unshift({
        label: 'Mark All Read',
        onClick: () =>
          markAllNotificationsAsRead(setLoading, handleError, () => {
            fetchNotifications()
          }),
      })
      options.unshift({
        label: 'Mark All Archived',
        onClick: () =>
          markAllNotificationsAsArchived(setLoading, handleError, () => {
            fetchNotifications()
          }),
      })
    }

    return options
  }, [notifications, isImpersonating])

  /**
   * Marks the notification as read or unread.
   * @param {object} notification
   */
  const handleToggleRead = async (notification) => {
    if (notification.unread) {
      await markNotificationAsRead(notification.id, setLoading, handleError, () => {
        fetchNotifications()
      })
    } else {
      await markNotificationAsUnread(notification.id, setLoading, handleError, () => {
        fetchNotifications()
      })
    }
  }

  /**
   * Marks the notification as archived or unarchived.
   * @param {object} notification
   */
  const handleToggleArchive = async (notification) => {
    if (notification.archived) {
      await markNotificationAsUnarchived(notification.id, setLoading, handleError, () => {
        fetchNotifications()
      })
    } else {
      await markNotificationAsArchived(notification.id, setLoading, handleError, () => {
        fetchNotifications()
      })
    }
  }

  /**
   * Handle a click on an announcement row: open the long-form modal if it has
   * body content, otherwise follow its link, otherwise just mark it read.
   */
  const handleAnnouncementClick = (announcement) => {
    if (announcement.hasBody) {
      announcementStore.openDetail(announcement.id)
      setMenuOpen(false)
      return
    }

    if (announcement.linkUrl) {
      announcementStore.markRead(announcement.id)
      setMenuOpen(false)
      if (announcement.linkUrl.startsWith('/')) {
        navigate(announcement.linkUrl)
      } else {
        window.open(announcement.linkUrl, '_blank', 'noopener,noreferrer')
      }
      return
    }

    announcementStore.markRead(announcement.id)
  }

  const renderAnnouncements = () => {
    if (bellAnnouncements.length === 0) return null

    return (
      <div className="border-b border-gray-200 pb-1">
        <span className="block px-2 pb-1 pt-2 text-[10px] font-black uppercase tracking-[0.12em] text-gray-500">
          Announcements
        </span>

        {_.map(bellAnnouncements, (announcement) => (
          <button
            key={announcement.id}
            type="button"
            onClick={() => handleAnnouncementClick(announcement)}
            className="group flex w-full flex-row items-start gap-3 px-2 py-3 text-left hover:bg-gray-50"
          >
            <div
              className={mergeClassNames(
                'mt-2 h-2 w-2 flex-none rounded-full',
                announcement.read ? 'bg-transparent' : 'bg-blue',
              )}
            />

            <div className="flex min-w-0 flex-1 flex-col gap-0.5">
              <span className="font-semibold">{announcement.title}</span>
              <span className="text-gray-700">{announcement.message}</span>
              {(announcement.hasBody || announcement.linkUrl) && (
                <span className="text-primary text-xs font-semibold">
                  {announcement.hasBody ? 'Read more' : announcement.linkLabel || 'Learn more'}
                </span>
              )}
              <span className="text-xs text-gray-500">
                {formatNotificationTime(announcement.createdAt)}
              </span>
            </div>
          </button>
        ))}
      </div>
    )
  }

  const renderNotifications = () => {
    if (notifications.length === 0) {
      // Don't show the empty state when announcements are filling the panel.
      if (bellAnnouncements.length > 0) return null

      return (
        <div className="flex flex-col items-center justify-center gap-3 py-10">
          <div className="flex size-12 items-center justify-center rounded-full bg-gray-200">
            <BellIcon className="size-6 fill-gray-400" />
          </div>
          <span className="text-gray-500">No notifications</span>
        </div>
      )
    }

    const configureUrl = (notification) => {
      if (notification.data?.linkTo === 'repair_order') {
        return `/dealer/${notification.data.dealerId}/repair-order/${notification.data.roId}`
      }
      if (notification.data?.linkTo === 'billing_cycle') {
        return `/dealer/${notification.data.dealerId}/billing`
      }
      return ''
    }

    return _.map(notifications, (notification, i) => (
      <Link
        onClick={() => {
          track(NOTIFICATION_CLICKED, { notification_type: notification.data?.linkTo })
          setMenuOpen(false)
        }}
        to={configureUrl(notification)}
        className={mergeClassNames(
          'group relative flex flex-row items-start gap-3 px-2 py-3 hover:bg-gray-50',
          i < notifications.length - 1 && 'border-b border-gray-200',
        )}
      >
        <div
          className={mergeClassNames(
            'mt-2 h-2 w-2 flex-none rounded-full',
            !notification.unread || notification.archived ? 'bg-transparent' : 'bg-blue',
          )}
        />

        <div className="flex min-w-0 flex-1 flex-col gap-0.5">
          <span className="text-left">{notification.description}</span>
          <span className="text-xs text-gray-500">
            {formatNotificationTime(notification.timestamp)}
          </span>
        </div>

        <div className="pointer-events-none absolute inset-y-0 right-0 w-40 bg-gradient-to-l from-gray-50 from-30% to-transparent opacity-0 transition-opacity group-focus-within:opacity-100 group-hover:opacity-100" />

        {/*
          WARP-567 — read, unread and archive all write to the *viewed* user's
          notifications, which an impersonated session cannot do. Hide the row actions
          rather than leave hover controls that only produce an error.
        */}
        {!isImpersonating && (
          <div className="pointer-events-none absolute right-2 top-2 opacity-0 transition-opacity group-focus-within:pointer-events-auto group-focus-within:opacity-100 group-hover:pointer-events-auto group-hover:opacity-100">
            <div className="flex items-center gap-0.5 rounded-md bg-[#fff] p-0.5 shadow-sm">
              {!notification.archived && (
                <>
                  <button
                    type="button"
                    className="rounded p-1 hover:bg-gray-200"
                    onClick={(event) => {
                      event.stopPropagation()
                      event.preventDefault()
                      handleToggleRead(notification)
                    }}
                    aria-label={notification.unread ? 'Mark as Read' : 'Mark as Unread'}
                  >
                    {notification.unread ? (
                      <EnvelopeOpenIcon
                        className="size-4 stroke-gray-600 stroke-2"
                        title="Mark read"
                      />
                    ) : (
                      <EnvelopeIcon
                        className="size-4 stroke-gray-600 stroke-2"
                        title="Mark unread"
                      />
                    )}
                  </button>

                  <button
                    type="button"
                    className="rounded p-1 hover:bg-gray-200"
                    onClick={(event) => {
                      event.stopPropagation()
                      event.preventDefault()
                      handleToggleArchive(notification)
                    }}
                    aria-label="Archive"
                  >
                    <ArchiveBoxIcon className="size-4 stroke-gray-600 stroke-2" title="Archive" />
                  </button>
                </>
              )}
              {notification.archived && (
                <button
                  type="button"
                  className="rounded p-1 hover:bg-gray-200"
                  onClick={(event) => {
                    event.stopPropagation()
                    event.preventDefault()
                    handleToggleArchive(notification)
                  }}
                  aria-label="Unarchive"
                >
                  <ArrowUturnLeftIcon
                    className="size-4 stroke-gray-600 stroke-2"
                    title="Unarchive"
                  />
                </button>
              )}
            </div>
          </div>
        )}
      </Link>
    ))
  }

  return (
    <Popover className="relative">
      <button
        className="relative flex size-8 items-center justify-center"
        onClick={() => {
          if (!isMenuOpen) {
            track(NOTIFICATION_MENU_OPENED, { unread_count: unreadNotificationsCount })
          }
          setMenuOpen(!isMenuOpen)
        }}
        type="button"
        ref={buttonRef}
      >
        <BellIcon className="mt-0.5 w-6 fill-white" />
        {totalUnreadCount > 0 && (
          <div
            className={joinClassNames(
              triggerAlert ? 'motion-safe:animate-bounce-once' : '',
              'bg-blue absolute -top-0.5 end-0 inline-flex h-4 min-w-[1rem] items-center justify-center rounded-full px-1 text-[10px] font-bold leading-none text-white',
            )}
          >
            {formatBadgeCount(totalUnreadCount)}
          </div>
        )}
      </button>

      {isMenuOpen && (
        <PopoverPanel
          className="absolute -right-14 z-10 flex w-screen translate-x-1 transition data-[closed]:translate-y-1 data-[closed]:opacity-0 data-[enter]:duration-200 data-[leave]:duration-150 data-[enter]:ease-out data-[leave]:ease-in sm:right-0 sm:max-w-max sm:translate-x-4 sm:px-4"
          ref={panelRef}
          static
        >
          <div className="flex w-screen flex-auto flex-col overflow-hidden rounded-3xl bg-white pt-3 text-sm leading-6 shadow-lg ring-1 ring-gray-900/5 sm:max-w-md">
            <div className="flex flex-row items-start justify-between border-b border-gray-200 px-4 pb-3 shadow-[0_4px_6px_-4px_rgba(0,0,0,0.08)]">
              <div className="flex flex-col">
                <span className="text-[10px] font-black uppercase tracking-[0.12em]">
                  Notifications
                </span>
                <span className="text-sm text-gray-600">
                  {_.find(FILTER_OPTIONS, (f) => f.value === filter)?.label}
                </span>
              </div>

              {hasNewNotifications && (
                <Button
                  background="bg-background"
                  outlined
                  label="Load New Notifications"
                  onClick={() => fetchNotifications()}
                  size="xs"
                />
              )}

              <div className="flex flex-row items-center gap-2 self-center">
                {loading && (
                  <ArrowPathIcon
                    className="motion-safe:animate-spin-slow size-4 self-center text-gray-500"
                    aria-label="Syncing notifications"
                  />
                )}

                <div className="flex items-center gap-0.5 rounded-md border border-gray-200 bg-white p-0.5">
                  {NOTIFICATION_SETTING_OPTIONS.length > 0 && (
                    <Menu as="div" className="relative overflow-visible">
                      <MenuButton
                        className="flex size-7 items-center justify-center rounded text-sm hover:bg-gray-200 focus:outline-none"
                        data-testid="notification-settings-dropdown"
                      >
                        <span className="sr-only">Open notification settings menu</span>
                        <EllipsisHorizontalIcon className="block h-5 stroke-black" />
                      </MenuButton>

                      <Transition
                        as={Fragment}
                        enter="transition ease-out duration-200"
                        enterFrom="transform opacity-0 scale-95"
                        enterTo="transform opacity-100 scale-100"
                        leave="transition ease-in duration-75"
                        leaveFrom="transform opacity-100 scale-100"
                        leaveTo="transform opacity-0 scale-95"
                      >
                        <MenuItems className="absolute right-0 z-20 -mt-1 w-48 origin-top-right rounded-md bg-white py-1 shadow-lg ring-1 ring-black/5 focus:outline-none">
                          {_.map(NOTIFICATION_SETTING_OPTIONS, (option) => (
                            <MenuItem key={option.label}>
                              {() => (
                                <button
                                  type="button"
                                  className="text-primary block w-full px-4 py-0.5 text-start text-sm hover:bg-gray-200"
                                  onClick={option.onClick}
                                >
                                  {option.label}
                                </button>
                              )}
                            </MenuItem>
                          ))}
                        </MenuItems>
                      </Transition>
                    </Menu>
                  )}

                  {NOTIFICATION_SETTING_OPTIONS.length > 0 && (
                    <div className="h-4 w-px bg-gray-200" aria-hidden="true" />
                  )}

                  <Menu as="div" className="relative overflow-visible">
                    <MenuButton
                      className="flex size-7 items-center justify-center rounded text-sm hover:bg-gray-200 focus:outline-none"
                      data-testid="notification-filter-dropdown"
                    >
                      <span className="sr-only">Open notification settings menu</span>
                      <FilterIcon className="block h-5 stroke-black" />
                    </MenuButton>

                    <Transition
                      as={Fragment}
                      enter="transition ease-out duration-200"
                      enterFrom="transform opacity-0 scale-95"
                      enterTo="transform opacity-100 scale-100"
                      leave="transition ease-in duration-75"
                      leaveFrom="transform opacity-100 scale-100"
                      leaveTo="transform opacity-0 scale-95"
                    >
                      <MenuItems className="absolute right-0 z-20 -mt-1 w-48 origin-top-right rounded-md bg-white py-1 shadow-lg ring-1 ring-black/5 focus:outline-none">
                        {_.map(FILTER_OPTIONS, (option) => (
                          <MenuItem key={option.label}>
                            {() => (
                              <button
                                type="button"
                                className="text-primary flex w-full flex-row items-center justify-between px-4 py-0.5 text-start text-sm hover:bg-gray-200"
                                onClick={() => {
                                  setCurrentPage(1)
                                  setFilter(option.value)
                                }}
                              >
                                {option.label}

                                {filter === option.value && (
                                  <>
                                    <span className="sr-only">Selected</span>
                                    <CheckIcon className="stroke-midnight block h-4 stroke-2" />
                                  </>
                                )}
                              </button>
                            )}
                          </MenuItem>
                        ))}
                      </MenuItems>
                    </Transition>
                  </Menu>
                </div>
              </div>
            </div>

            <div className="flex h-screen flex-col overflow-auto px-2 sm:h-full sm:max-h-80">
              {renderAnnouncements()}
              {renderNotifications()}
            </div>

            {nextPage && (
              <button
                className="text-primary hover:text-midnight w-full border-t border-gray-200 py-2 text-center text-xs font-bold uppercase tracking-widest shadow-[0_-4px_6px_-4px_rgba(0,0,0,0.08)] transition-colors duration-200 ease-out hover:bg-gray-200"
                disabled={loadingMore}
                onClick={() => {
                  setCurrentPage(currentPage + 1)
                  getNotifications(
                    nextPage,
                    currentPage + 1,
                    setLoadingMore,
                    handleError,
                    setNotifications,
                    true,
                  )
                }}
                type="button"
              >
                See More
              </button>
            )}
          </div>
        </PopoverPanel>
      )}
    </Popover>
  )
})

export default Notifications
